import classnames from 'classnames';
import { cloneDeep, isString } from 'lodash-es';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { GridColumn, GridOption, GridSortType } from 'types/grid';
import { Button } from '../Button';
import Checkbox from '../Checkbox';
import Text from '../Text';
import { getGridPageArray } from './utils';

import { ChevronDownOutlineIcon, SelectorOutlineIcon } from 'assets/svgs';

interface Props {
  gridColumns: GridColumn[];
  gridData: Record<string, any>[];
  filterValue?: Record<string, any>;
  searchValue?: string;
  currentPage?: number;
  setCurrentPage?: Dispatch<SetStateAction<number>>;
  checkedRows?: number[];
  setCheckedRows?: Dispatch<SetStateAction<number[]>>;
  options?: GridOption;
  className?: string;
  isDisabled?: (data: Record<string, any>) => boolean;
}

const Grid = ({
  gridColumns,
  gridData,
  filterValue,
  searchValue,
  currentPage,
  setCurrentPage,
  checkedRows,
  setCheckedRows,
  options = {},
  className,
  isDisabled,
}: Props) => {
  const [innerGridData, setInnerGridData] = useState(cloneDeep(gridData));
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [columnSortType, setColumnSortType] = useState<GridSortType[]>(
    Array.from({ length: gridColumns.length }, () => 'none')
  );
  const [pageArray, setPageArray] = useState<(string | number)[]>(
    getGridPageArray(options?.usePagination?.maxPage ?? 1, currentPage ?? 1)
  );

  function onCheckAll(isChecked: boolean) {
    if (!setCheckedRows) {
      return;
    }
    if (!isChecked) {
      setCheckedRows([]);
    } else {
      const enabledRows = gridData
        .map((data, index) => ({ data, index }))
        .filter(({ data }) => !isDisabled?.(data))
        .map(({ index }) => index);
      setCheckedRows(enabledRows);
    }
    setIsAllChecked(isChecked);
  }


  function onCheckRow(index: number) {
    if (!setCheckedRows) {
      return;
    }
    if (isDisabled?.(gridData[index])) {
      return;
    }
    if (checkedRows?.includes(index)) {
      setCheckedRows((prev) => prev.filter((value) => value !== index));
    } else {
      setCheckedRows((prev) => [...prev, index]);
    }
  }

  function filterData() {
    if (options?.useFilter && filterValue) {
      const filterKey = Object.keys(filterValue);
      return gridData.filter((data) => {
        return filterKey.every((key) => {
          if (!data[key]) {
            return true;
          }
          return `${data[key]}`.includes(`${filterValue[key]}`);
        });
      });
    }
    return cloneDeep(gridData);
  }

  function searchData() {
    if (options?.useSearch?.use && searchValue) {
      const searchKey = options?.useSearch?.searchKeys ?? [];
      return gridData.filter((data) => {
        return searchKey.some((key) => {
          if (!data[key]) {
            return true;
          }
          return `${data[key]}`.includes(searchValue);
        });
      });
    }
    return cloneDeep(gridData);
  }

  function sortData(key: string, idx: number) {
    if (columnSortType[idx] === 'none') {
      setInnerGridData((prev) =>
        prev.sort((a, b) => {
          if (isString(a[key])) {
            return (a[key] as string).toLowerCase().localeCompare((b[key] as string).toLowerCase());
          }
          return (a[key] as number) - (b[key] as number);
        })
      );
      setColumnSortType((prev) => [...prev.slice(0, idx), 'asc', ...prev.slice(idx + 1)]);
    } else if (columnSortType[idx] === 'asc') {
      setInnerGridData((prev) =>
        prev.sort((a, b) => {
          if (isString(b[key])) {
            return (b[key] as string).toLowerCase().localeCompare((a[key] as string).toLowerCase());
          }
          return (b[key] as number) - (a[key] as number);
        })
      );
      setColumnSortType((prev) => [...prev.slice(0, idx), 'desc', ...prev.slice(idx + 1)]);
    } else {
      setInnerGridData(cloneDeep(gridData));
      setColumnSortType((prev) => [...prev.slice(0, idx), 'none', ...prev.slice(idx + 1)]);
    }
  }

  function onClickPrev() {
    if (currentPage! <= 1) {
      return;
    }
    setCurrentPage!((prev) => (prev <= 1 ? prev : prev - 1));
  }

  function onClickNext() {
    const maxPage = options?.usePagination?.maxPage ?? 1;
    setCurrentPage!((prev) => (prev >= maxPage ? prev : prev + 1));
  }

  function onClickPageNum(page: number | string) {
    if (typeof page === 'string') {
      return;
    }
    setCurrentPage!(page);
  }

  useEffect(() => {
    const enabledRowCount = gridData.filter(data => !isDisabled?.(data)).length;
    setIsAllChecked(checkedRows?.length === enabledRowCount && enabledRowCount > 0);
  }, [checkedRows, gridData, isDisabled]);
  
  useEffect(() => {
    setInnerGridData(cloneDeep(gridData));
  }, [gridData]);
  
  useEffect(() => {
    setInnerGridData(filterData);
  }, [filterValue]);

  useEffect(() => {
    setInnerGridData(searchData);
  }, [searchValue]);

  useEffect(() => {
    const maxPage = options?.usePagination?.maxPage ?? 1;
    const curPage = currentPage ?? 1;
    setPageArray(getGridPageArray(maxPage, curPage));
  }, [options?.usePagination?.maxPage, currentPage]);

  return (
    <div className={classnames('flex h-full w-full flex-col justify-between', className)}>
      <div className="h-full w-full max-w-full overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 table-header-group h-12 w-full border-b border-b-border-primary bg-bg-layer2">
            <tr className="table-row">
              {options?.useCheckbox && (
                <th className="pl-6 px-5" style={{ width: 16 }}>
                  {!options?.hideSelectAllCheckbox && (
                    <Checkbox inputKey="check-all" value={isAllChecked} onChange={onCheckAll} />
                  )}
                </th>
              )}
              {gridColumns.map((col, idx) => (
                <th style={{ maxWidth: col.width, width: col.width }} className="px-5" key={col.key}>
                  <div className="flex h-full w-full flex-row items-center gap-2">
                    <Text weight="regular" color="tertiary" className="text-start">
                      {col.title}
                    </Text>
                    {options?.useSort?.use && options?.useSort?.sortKeys?.includes(col.key) && (
                      <button type="button" className="h-3 w-3" onClick={() => sortData(col.key, idx)}>
                        {columnSortType[idx] === 'none' ? (
                          <SelectorOutlineIcon className="[&_path]:stroke-text-tertiary} h-3 w-3" />
                        ) : (
                          <ChevronDownOutlineIcon
                            className={classnames('[&_path]:stroke-text-tertiary} h-3 w-3', {
                              'rotate-180': columnSortType[idx] === 'asc'
                            })}
                          />
                        )}
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {innerGridData.map((data, index) => (
              <tr
                key={index}
                className={classnames(
                  'table-row h-12',
                  options?.rowClassName?.(data, index),
                  {
                    'bg-system50 hover:bg-slate-50':
                      options?.useCheckbox && checkedRows?.includes(index),
                    'opacity-50 pointer-events-none': isDisabled?.(data),
                    'cursor-pointer': options?.useCheckbox && !isDisabled?.(data),
                  }
                )}
                onClick={() => {
                  if (options?.useCheckbox && !isDisabled?.(data) && setCheckedRows) {
                    onCheckRow(index);
                  }
                }}
              >
                {options?.useCheckbox && (
                  <td 
                    className="h-12 py-0 pl-6" 
                    style={{ width: 16 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      inputKey={`grid-row-${index}`}
                      value={!!checkedRows?.includes(index)}
                      onChange={() => onCheckRow(index)}
                      disabled={isDisabled?.(data)}
                    />
                  </td>
                )}
                {gridColumns.map((col, idx) => (
                  <td
                    key={idx}
                    className="h-12 px-5 py-0"
                    style={{ maxWidth: col.width, width: col.width, minWidth: col.width }}
                    title={`${data[col.key]}`}
                  >
                    {options?.customCell?.[col.key] ? (
                      options?.customCell?.[col.key](data[col.key], data, index)
                    ) : (
                      <Text
                        className="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                        weight="regular"
                      >{`${data[col.key]}`}</Text>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {innerGridData.length > 0 && options?.usePagination && options.usePagination.maxPage && currentPage && (
        <div className="flex flex-row items-center justify-center gap-4 p-4">
          <Button
            className={classnames({ invisible: currentPage <= 1 })}
            type="secondary"
            size="small"
            text="Prev"
            onClick={onClickPrev}
          />
          {pageArray.map((page, index) => (
            <button
              className={classnames(
                'flex h-8 w-8 items-center justify-center rounded',
                {
                  'bg-black': page === currentPage
                },
                {
                  'cursor-default': page === '...'
                }
              )}
              key={`grid_pagination_${page}_${index}`}
              type="button"
              onClick={() => onClickPageNum(page)}
            >
              <Text
                className="select-none"
                color={page === currentPage ? 'white' : 'black'}
                weight="regular"
                size="body2"
              >
                {page}
              </Text>
            </button>
          ))}
          <Button
            className={classnames({ invisible: currentPage >= options.usePagination.maxPage })}
            type="secondary"
            size="small"
            text="Next"
            onClick={onClickNext}
          />
        </div>
      )}
    </div>
  );
};

export default Grid;
